import { useRef, useEffect } from "react";
import { X, AlertCircle } from "lucide-react";
import MessageInput from "./MessageInput"; // Assuming MessageInput is in the same directory
import WarningBanner from "./WarningBanner";
// Placeholder Message Component
// In a real application, this would be in its own file (e.g., Message.jsx)
const Message = ({
  content,
  isUser,
  timestamp,
  avatar,
  username,
  pending,
  failed,
}) => {
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`flex items-end ${
          isUser ? "flex-row-reverse" : "flex-row"
        } space-x-2`}
      >
        {!isUser && avatar && (
          <img
            src={
              avatar ||
              `https://placehold.co/40x40/7B189F/white?text=${
                username?.charAt(0) || "U"
              }`
            }
            alt={username || "User"}
            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
          />
        )}
        <div
          className={`max-w-xs md:max-w-md rounded-lg p-3 relative ${
            isUser ? "bg-[#7B189F] text-white" : "bg-gray-200 text-gray-800"
          } ${pending ? "opacity-70" : ""}`}
        >
          <p>{content}</p>
          <div
            className={`text-xs mt-1 ${
              isUser ? "text-gray-200" : "text-gray-500"
            } text-right`}
          >
            {timestamp}
            {pending && <span className="ml-2">Sending...</span>}
            {failed && <span className="ml-2 text-red-300">Failed!</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

const style = document.createElement("style");
style.textContent = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;
document.head.appendChild(style);

const MobileChat = ({
  messages = [],
  onSendMessage,
  isConnected = false,
  activeRoomRef,
  handleCloseChat,
  recipientName = "User",
  recipientAvatar = "User",
  connectionStatus = "disconnected",
  connectionInfo = {},
  onReconnect,
  onClose,
}) => {
  const messagesEndRef = useRef(null); // Add a ref for scrolling to the bottom

  useEffect(() => {
    // Scroll to the bottom when messages update
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
    }
  };

  return (
    // Main container: flex column, full height
    <div className="flex flex-col h-4/5 bg-white overflow-hidden">
      {/* Connection Status Bar */}
      {connectionStatus !== "connected" && (
        <div
          className={`${connectionInfo.bg} border-b px-4 py-2 flex-shrink-0`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <connectionInfo.icon
                size={16}
                className={`${connectionInfo.color} ${
                  connectionStatus === "connecting" ? "animate-spin" : ""
                }`}
              />
              <span className={`text-sm font-medium ${connectionInfo.color}`}>
                {connectionInfo.text}
              </span>
            </div>
            {connectionStatus !== "connected" && onReconnect && (
              <button
                onClick={onReconnect}
                className="text-sm px-3 py-1 bg-white rounded border hover:bg-gray-50"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}

      {/* Chat Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <img
                src={recipientAvatar}
                alt={recipientName}
                className="w-10 h-10 rounded-full object-cover"
              />
              {isConnected && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{recipientName}</h3>
              <p
                className={`text-sm ${
                  isConnected ? "text-green-500" : "text-gray-500"
                }`}
              >
                {isConnected ? "● Active" : "● Offline"}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-gray-600 font-medium">
            {new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      </div>
      {activeRoomRef.current && (
        <WarningBanner
          message="This chat stays private and smooth And will end in 10 minutes."
          closeChat={handleCloseChat}
          chatId={activeRoomRef.current}
        />
      )}

      {/* Messages - Scrollable Section */}
      <div className="flex-1 overflow-y-auto pt-10 pb-10 px-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>No messages yet. Start the conversation!</p>
            {!isConnected && (
              <p className="text-red-500 text-sm mt-2">
                Not connected to server
              </p>
            )}
          </div>
        ) : (
          messages.map((message) => (
            <Message
              key={message.id}
              content={message.content}
              isUser={message.isUser}
              timestamp={message.timestamp}
              avatar={recipientAvatar}
              username={message.username}
              pending={message.pending}
              failed={message.failed}
            />
          ))
        )}
        <div ref={messagesEndRef} /> {/* For auto-scrolling */}
      </div>

      {/* Message Input */}
      <div className="flex-shrink-0">
        <MessageInput
          onSendMessage={onSendMessage}
          disabled={!isConnected}
          placeholder={isConnected ? "Type a message..." : "Connecting..."}
        />
      </div>
    </div>
  );
};

// Mobile Chat Selection Screen Component
const MobileChatSelection = ({
  allGroups = [],
  rooms = [],
  onGroupSelect,
  onRoomSelect,
  onChatSelect,
  selectedGroup,
  isLoading = false,
  error = "",
  isConnected = false,
  connectionStatus = "disconnected",
  connectionInfo = {},
  onReconnect,
}) => {
  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {connectionStatus !== "connected" && (
        <div
          className={`${
            connectionInfo.bg || "bg-gray-100"
          } border-b px-4 py-2 flex-shrink-0`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {connectionInfo.icon && (
                <connectionInfo.icon
                  size={16}
                  className={`${connectionInfo.color || "text-gray-700"} ${
                    connectionStatus === "connecting" ? "animate-spin" : ""
                  }`}
                />
              )}
              <span
                className={`text-sm font-medium ${
                  connectionInfo.color || "text-gray-700"
                }`}
              >
                {connectionInfo.text || "Connecting..."}
              </span>
            </div>
            {connectionStatus !== "connected" && onReconnect && (
              <button
                onClick={onReconnect}
                className="text-sm px-3 py-1 bg-white rounded border hover:bg-gray-50"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border-b border-gray-200 px-4 py-4 flex-shrink-0">
        <h1 className="text-xl font-semibold text-gray-900">Chat</h1>
      </div>

      {allGroups && allGroups.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {allGroups.map((group) => (
              <button
                key={group.vibe_id}
                onClick={() => {
                  if (!isLoading) {
                    onGroupSelect(group);
                  }
                }}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors border border-black
                  ${
                    selectedGroup?.vibe_id === group.vibe_id
                      ? "text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                style={
                  selectedGroup?.vibe_id === group.vibe_id
                    ? { backgroundColor: "#7B189F" }
                    : {}
                }
              >
                {group.emoji + " " + group.title}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0">
        {/*
          isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center text-gray-500">
              <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
              <p>Loading chats...</p>
            </div>
          </div>

        )*/}
        {selectedGroup &&
          selectedGroup.chat_profiles &&
          selectedGroup.chat_profiles.length > 0 &&
          selectedGroup.chat_profiles.map((chat_profile) => {
            return (
              <div
                onClick={() => onChatSelect(chat_profile)}
                className={`flex items-center p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors`}
              >
                {/* Chat info */}
                <div className="flex-1 min-w-0">
                  <div className="flex relative items-center justify-between mb-1">
                    <div className="absolute bottom-1 left-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                    <h3 className="font-semibold ml-5 text-gray-900 truncate">
                      {chat_profile.chat_name}
                    </h3>
                  </div>
                </div>
              </div>
            );
          })}

        {error ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center text-red-500">
              <AlertCircle className="mx-auto mb-2" size={24} />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        ) : rooms.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center text-gray-500">
              <p>Galli feels quiet right now. </p>
              <p className="text-sm mt-1">
                {selectedGroup
                  ? `Come back soon for some fresh chatter and gupshup!`
                  : "Try other vibes - Might not be the match—but a little gupshup never hurt."}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white">
            {rooms.map((room) => (
              <div
                key={room.id}
                onClick={() => onRoomSelect(room)}
                className="flex items-center p-4 border-b border-gray-100 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center space-x-3">
                  <img
                    src={
                      room.avatar ||
                      "/placeholder.svg?height=48&width=48&query=user"
                    }
                    alt={room.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />

                  {room.isOnline && (
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                  )}
                  <h3 className="font-semibold text-gray-900 truncate">
                    {room.name}
                  </h3>
                </div>
                {room.unreadCount > -1 && (
                  <div
                    className="ml-auto text-white text-xs rounded-full w-6 h-5 flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "#6B189F" }}
                  >
                    {room.unreadCount > 98 ? "99+" : room.unreadCount}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileChat;
export { MobileChatSelection };
